import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../../api/http';
import InvoiceApi, {
	getFutureAndGeneratedInvoicesQueryKey,
	getUseInvoiceQueryKey,
} from '../../../api/InvoiceApi';
import { getUsePolicyQueryKey } from '../../../api/PolicyApi';
import { getInvoicesForPolicyholderQueryKey } from '../../../api/PolicyholderApi';
import { AuthContext, AuthData } from '../../../context/auth';
import {
	CancellationContext,
	CancellationData,
} from '../../../context/CancellationProvider';
import { ConfigContext, ConfigData } from '../../../context/config';
import {
	FETCH_INVOICE_FAILURE,
	PAY_INVOICE_FAILURE,
	PAY_INVOICE_SUCCESS,
	WRITE_OFF_INVOICE_FAILURE,
	WRITE_OFF_INVOICE_SUCCESS,
} from '../../../data/labelsAndMessages';
import PaymentsList from '../../PaymentsList';
import { errorToast, successToast } from '../../Toast';

interface Props {
	authData: AuthData;
	configData: ConfigData;
	cancellationData: CancellationData;
	reinstatementInvoice?: boolean;
}

const InvoiceView = (props: Props) => {
	const params = useParams();
	const { invoiceId } = params;
	const { authData, cancellationData, reinstatementInvoice } = props;
	const authToken = authData?.auth?.authorizationToken;
	const { selectedCancellation, setLoading, policy } = cancellationData;
	const queryClient = useQueryClient();
	const [invoice, setInvoice] = useState(null);

	const invoiceLocator = reinstatementInvoice
		? selectedCancellation.reinstatement.invoiceLocator
		: selectedCancellation.invoiceLocator;

	useEffect(() => {
		const fetchInvoice = async () => {
			setLoading(true);
			let invoiceObj = {};
			try {
				const resp = await InvoiceApi.fetchInvoice(
					authData.auth.authorizationToken,
					invoiceLocator,
				);
				invoiceObj = resp.data;
			} catch (error) {
				errorToast(FETCH_INVOICE_FAILURE());
				setLoading(false);
				if (await handleUnauthorizedResponse(error, authData)) {
					return;
				}
			}
			setInvoice(invoiceObj);
			setLoading(false);
		};
		fetchInvoice();
	}, [invoiceId]);

	const refetchData = () => {
		// invalidate policy
		queryClient.invalidateQueries(
			getUsePolicyQueryKey(policy.locator, authToken),
		);

		// invalidate invoices
		queryClient.invalidateQueries(
			getFutureAndGeneratedInvoicesQueryKey(policy.locator, authToken),
		);
		queryClient.invalidateQueries(
			getInvoicesForPolicyholderQueryKey(authToken, policy.policyholderLocator),
		);

		// invalidate invoice
		queryClient.invalidateQueries(
			getUseInvoiceQueryKey(authToken, invoice.locator),
		);
	};

	const payInvoice = async (
		_policyLocator: string,
		locator: string,
		req: any,
	) => {
		setLoading(true);
		try {
			await InvoiceApi.payInvoice(
				authData.auth.authorizationToken,
				locator,
				req,
			);
			refetchData();
			successToast(PAY_INVOICE_SUCCESS());
			setLoading(false);
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast(PAY_INVOICE_FAILURE());
		} finally {
			setLoading(false);
		}
	};

	const reversePayment = async (paymentLocator: string) => {
		setLoading(true);
		try {
			await InvoiceApi.reversePayment(
				authData.auth.authorizationToken,
				paymentLocator,
			);
			refetchData();
			successToast('Successfully reversed payment');
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast('Failed to pay invoice');
		} finally {
			setLoading(false);
		}
	};

	const writeOffInvoice = async (locator: string) => {
		setLoading(true);
		try {
			await InvoiceApi.writeOffInvoice(
				authData.auth.authorizationToken,
				locator,
			);
			refetchData();
			successToast(WRITE_OFF_INVOICE_SUCCESS());
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast(WRITE_OFF_INVOICE_FAILURE());
		} finally {
			setLoading(false);
		}
	};

	return (
		<PaymentsList
			invoice={invoice}
			onPayment={payInvoice}
			onReverse={reversePayment}
			onWriteOff={writeOffInvoice}
			callback={null}
			currency={policy?.currency}
		/>
	);
};

function withContext(Component: any) {
	return (
		props: Omit<Props, 'configData' | 'authData' | 'cancellationData'>,
	) => (
		<ConfigContext.Consumer>
			{(configData) => (
				<AuthContext.Consumer>
					{(authData) => (
						<CancellationContext.Consumer>
							{(cancellationData) => (
								<Component
									{...props}
									configData={configData}
									authData={authData}
									cancellationData={cancellationData}
								/>
							)}
						</CancellationContext.Consumer>
					)}
				</AuthContext.Consumer>
			)}
		</ConfigContext.Consumer>
	);
}

export default withContext(InvoiceView);
